/**
 * 간편 접수 폼. 고객이 휴대폰으로 열어 30초 안에 끝내는 화면이라
 * 칸은 여섯 개뿐이고 필수는 둘(이름·연락처) 이다.
 *
 * 토스트를 쓰지 않는다 — 접수 화면은 이 한 장이 전부라 결과가 화면 안에 남아야 한다.
 */

import { FormEvent, ReactNode, useState, useTransition } from "react";

import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { SimpleSelect } from "@buildpy/ui/select";
import { Textarea } from "@buildpy/ui/textarea";
import { directionJosa } from "@buildpy/core/lib/josa";
import { submitIntake } from "@/api/actions/customer";
import { DESIGNER_FIELD, IntakeDesigner } from "@buildpy/core/modules/customer/intake-designer";
import type { IntakeMode } from "@buildpy/core/modules/customer/intake-detail";
import {
  CASE_FIELD,
  FROM_FIELD,
  FROM_PORTFOLIO,
  FROM_REFERRAL,
  type IntakeOrigin,
  PAGE_FIELD,
  REF_FIELD,
} from "@buildpy/core/modules/customer/intake-page";
import { SITE_FIELD } from "@buildpy/core/modules/customer/intake-site";
import {
  BUDGET_BAND_LABEL,
  BUDGET_BANDS,
  CALL_TIME_LABEL,
  CALL_TIMES,
  WORK_SCOPE_LABEL,
  WORK_SCOPES,
} from "@buildpy/core/modules/customer/types";
import AddressField from "@/modules/customer/ui/address-field";
import type { CodeOption } from "@buildpy/core/modules/org/codes";

// 옆 입력칸과 같은 44px 높이(size="lg")·16px 글자.
const selectClass = "text-base";

interface Props {
  slug: string;
  orgName: string;
  /** 조직이 설정에서 고친 표기. 만들어 둔 것을 받기만 한다(저장소를 부르지 않는다). */
  residenceOptions: CodeOption[];
  areaBandOptions: CodeOption[];
  // 공개 담당 후보(이름·소개만). 비어 있으면 칸을 그리지 않는다.
  designers: IntakeDesigner[];
  // 회사 설정의 폼 모드 — 상세면 공사 종류·예산대·통화 가능 시간 칸이 붙는다.
  mode?: IntakeMode;
  // 공사 안내문 QR 의 현장 id — 화면에 보이지 않고 hidden 으로만 싣는다(서버가 다시 확인한다).
  site?: string | null;
  // 공개 시공사례 페이지의 「상담 문의」 표시·링크 주소·사례 id · 소개 링크 주소 — site 처럼 hidden 으로만 싣는다.
  origin?: IntakeOrigin | null;
}

const Field = ({
  label,
  name,
  required,
  hint,
  children,
}: {
  label: string;
  name: string;
  required?: boolean;
  hint?: string;
  children: ReactNode;
}) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-sm font-medium" htmlFor={name}>
      {label}
      {required && <span className="ml-0.5 text-destructive">*</span>}
    </label>
    {children}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

const IntakeForm = ({
  slug,
  orgName,
  residenceOptions,
  areaBandOptions,
  designers,
  mode = "simple",
  site,
  origin,
}: Props) => {
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setError("");

    startTransition(async () => {
      try {
        const result = await submitIntake(slug, form);
        if (result.ok) setDone(true);
        else setError(result.message);
      } catch {
        setError("접수하지 못했습니다. 잠시 후 다시 시도해 주세요.");
      }
    });
  };

  if (done) {
    return (
      <SectionCard as="div" className="text-center" padding="lg">
        <h2 className="text-lg font-semibold">접수됐습니다</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          곧 연락드립니다. 급하시면 {orgName}{directionJosa(orgName)} 전화 주세요.
        </p>
      </SectionCard>
    );
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={onSubmit}>
      {/* 허니팟 — 사람에게는 보이지 않고 봇만 채운다. 자동완성도 막는다. */}
      <div aria-hidden className="hidden">
        <label htmlFor="company_site">회사 홈페이지</label>
        {/* eslint-disable-next-line no-restricted-syntax -- raw-input: 허니팟(scripts/design-rules.mjs lineExcept) */}
        <input
          autoComplete="off"
          id="company_site"
          name="company_site"
          tabIndex={-1}
          type="text"
        />
      </div>

      {site && <input name={SITE_FIELD} type="hidden" value={site} />}
      {origin?.from && <input name={FROM_FIELD} type="hidden" value={FROM_PORTFOLIO} />}
      {origin?.page && <input name={PAGE_FIELD} type="hidden" value={origin.page} />}
      {origin?.case && <input name={CASE_FIELD} type="hidden" value={origin.case} />}
      {/* from 은 한 값뿐이라 공개 페이지 표시와 겹치지 않는다. */}
      {origin?.referral && <input name={FROM_FIELD} type="hidden" value={FROM_REFERRAL} />}
      {origin?.referral && <input name={REF_FIELD} type="hidden" value={origin.referral} />}

      <fieldset className="flex flex-col gap-4" disabled={pending}>
        <Field label="이름" name="name" required>
          <Input
            autoComplete="name"
            id="name"
            inputSize="lg"
            maxLength={50}
            name="name"
            placeholder="홍길동"
            required
          />
        </Field>

        <Field label="연락처" name="phone" required>
          <Input
            autoComplete="tel"
            id="phone"
            inputMode="tel"
            inputSize="lg"
            maxLength={30}
            name="phone"
            placeholder="010-0000-0000"
            required
            type="tel"
          />
        </Field>

        <Field
          hint="아직 안 정하셨으면 비워 두셔도 됩니다"
          label="현장 주소"
          name="site_address">
          <AddressField inputSize="lg" placeholder="서울 강서구 마곡동 OO아파트 101동 1001호" />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="거주형태" name="residence">
            <SimpleSelect
              className={selectClass}
              defaultValue=""
              id="residence"
              name="residence"
              options={[{ value: "", label: "선택 안 함" }, ...residenceOptions]}
              size="lg"
            />
          </Field>

          <Field label="평형대" name="area_band">
            <SimpleSelect
              className={selectClass}
              defaultValue=""
              id="area_band"
              name="area_band"
              options={[{ value: "", label: "선택 안 함" }, ...areaBandOptions]}
              size="lg"
            />
          </Field>
        </div>

        {mode === "detail" && (
          <>
            <Field label="공사 종류 (선택)" name="work_scope">
              <SimpleSelect
                className={selectClass}
                defaultValue=""
                id="work_scope"
                name="work_scope"
                options={[{ value: "", label: "선택 안 함" }, ...WORK_SCOPES.map((value) => ({ value, label: WORK_SCOPE_LABEL[value] }))]}
                size="lg"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="예산대 (선택)" name="budget_band">
                <SimpleSelect
                  className={selectClass}
                  defaultValue=""
                  id="budget_band"
                  name="budget_band"
                  options={[{ value: "", label: "선택 안 함" }, ...BUDGET_BANDS.map((value) => ({ value, label: BUDGET_BAND_LABEL[value] }))]}
                  size="lg"
                />
              </Field>

              <Field label="통화 가능 시간 (선택)" name="call_time">
                <SimpleSelect
                  className={selectClass}
                  defaultValue=""
                  id="call_time"
                  name="call_time"
                  options={[{ value: "", label: "선택 안 함" }, ...CALL_TIMES.map((value) => ({ value, label: CALL_TIME_LABEL[value] }))]}
                  size="lg"
                />
              </Field>
            </div>
          </>
        )}

        {designers.length > 0 && (
          <Field label="담당 디자이너 (선택)" name={DESIGNER_FIELD}>
            <SimpleSelect
              className={selectClass}
              defaultValue=""
              id={DESIGNER_FIELD}
              name={DESIGNER_FIELD}
              options={[
                { value: "", label: "상관없음" },
                ...designers.map((designer) => ({
                  value: designer.id,
                  label: designer.intro ? `${designer.name} — ${designer.intro}` : designer.name,
                })),
              ]}
              size="lg"
            />
          </Field>
        )}

        <Field
          hint="공사 범위·희망 일정·예산 등 무엇이든 좋습니다"
          label="요청 사항"
          name="memo">
          <Textarea
            className="min-h-28 text-lg"
            id="memo"
            maxLength={1000}
            name="memo"
            placeholder="주방·욕실 위주로 상담받고 싶습니다."
          />
        </Field>
      </fieldset>

      {error && (
        <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
          {error}
        </p>
      )}

      <Button className="w-full" disabled={pending} size="cta" type="submit" variant="black">
        {pending ? "접수 중…" : "상담 신청하기"}
      </Button>

      <p className="text-center text-xs text-muted-foreground">
        보내 주신 정보는 상담 목적으로만 사용합니다.
      </p>
    </form>
  );
};

export default IntakeForm;
